// Components==============
import {
  faGripLines,
  faLock,
  faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddInput from 'components/UI/AddInput/AddInput';
import Button from 'components/UI/Button/Button';
import { motion } from 'framer-motion';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { framerCollapse } from 'utilities/FramerAnimations';
import { generateTrainingUrl } from 'utilities/generateTrainingUrl';
import { TrainingBuilderContext } from '../../TrainingBuilder';
import ConfirmModal from './ConfirmModal/ConfirmModal';
import './Menu.module.scss';
// =========================

export default function Menu() {
  const translate = useTranslate();
  const {
    handleSave,
    setEditorView,
    editorView,
    sessions,
    currentSession,
    setCurrentSession,
    handleAddSession,
    handleAddLesson,
    handleReorderSession,
    handleReorderLesson,
    training,
    loading,
  } = useContext(TrainingBuilderContext);

  const [saveLoader, setSaveLoader] = useState(false);
  const [saveAndViewLoader, setSaveAndViewLoader] = useState(false);

  const { setModal } = useRootModal();

  function save(andView?: boolean) {
    handleSave(andView);

    if (!andView) return setSaveLoader(true);
    if (andView) {
      setSaveLoader(false);
      setSaveAndViewLoader(true);
    }
  }

  function handleConfirmModal() {
    return setModal(<ConfirmModal handleSave={save} />);
  }

  const [displayOverflow, setDisplayOverflow] = useState(false);

  useEffect(() => {
    setDisplayOverflow(false);

    const timer = setTimeout(() => {
      setDisplayOverflow(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [currentSession]);

  return (
    <div styleName="wrapper">
      <button
        styleName={`training-info ${editorView === 'training' ? 'active' : ''}`}
        type="button"
        onClick={() => setEditorView('training')}
      >
        {translate('course.terms.course-information')}
      </button>
      {!!sessions.length && (
        <DragDropContext onDragEnd={handleReorderSession}>
          <Droppable droppableId="sessions">
            {(sessionProvided) => (
              <div
                {...sessionProvided.droppableProps}
                ref={sessionProvided.innerRef}
                styleName="sessions"
                style={{ overflow: displayOverflow ? 'auto' : 'hidden' }}
              >
                {sessions.map((session, i) => (
                  <Draggable
                    key={i}
                    draggableId={session.id.toString()}
                    isDragDisabled={!!session.is_published}
                    index={i}
                  >
                    {(sessionDragProvided) => (
                      <div
                        ref={sessionDragProvided.innerRef}
                        {...sessionDragProvided.draggableProps}
                      >
                        <div
                          onClick={() => {
                            setEditorView('session');
                            setCurrentSession(session.id);
                          }}
                          styleName={`session ${
                            currentSession === session.id ? 'active' : ''
                          }`}
                        >
                          <div
                            styleName="top"
                            style={{
                              marginBottom:
                                session.id === currentSession ? '1em' : 0,
                            }}
                          >
                            <div {...sessionDragProvided.dragHandleProps}>
                              <FontAwesomeIcon
                                icon={
                                  session.is_published ? faLock : faGripLines
                                }
                              />
                            </div>
                            <p styleName="title">{session.title}</p>
                            {!!session.lessons?.length &&
                              !session.is_published && (
                                <p styleName="lesson">
                                  {session?.lessons?.length}
                                </p>
                              )}
                          </div>

                          <DragDropContext onDragEnd={handleReorderLesson}>
                            <Droppable droppableId="lessons">
                              {(provided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                >
                                  <motion.div
                                    animate={
                                      session.id === currentSession
                                        ? 'animate'
                                        : 'initial'
                                    }
                                    initial="initial"
                                    // @ts-ignore
                                    variants={framerCollapse}
                                    styleName="lessons"
                                  >
                                    {session?.lessons
                                      ?.sort(
                                        (a, b) =>
                                          (a.order_index || 0) -
                                          (b.order_index || 0)
                                      )
                                      .map((lesson, index) => (
                                        <Draggable
                                          key={`${session.id}${lesson.id}`}
                                          draggableId={`${session.id}.${lesson.id}.overview`}
                                          index={index}
                                          isDragDisabled={!!lesson.is_published}
                                        >
                                          {(dragProvided) => (
                                            <a
                                              href={`#${translate(
                                                'course.terms.lesson'
                                              )} ${index + 1}`}
                                              ref={dragProvided.innerRef}
                                              {...dragProvided.draggableProps}
                                            >
                                              <div styleName="lesson">
                                                <p>{lesson.title}</p>
                                                <div
                                                  styleName="icon-wrapper"
                                                  {...dragProvided.dragHandleProps}
                                                >
                                                  <FontAwesomeIcon
                                                    icon={
                                                      lesson.is_published
                                                        ? faLock
                                                        : faGripLines
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </a>
                                          )}
                                        </Draggable>
                                      ))}

                                    {provided.placeholder}
                                    <AddInput
                                      onSubmit={(val) => handleAddLesson(val)}
                                      placeholder={`${translate(
                                        'course.terms.lesson'
                                      )}...`}
                                    />
                                  </motion.div>
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {sessionProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div styleName="new-session">
        <AddInput
          onSubmit={(val) => handleAddSession(val)}
          placeholder={`${translate('course.terms.session')}...`}
        />
      </div>
      <div styleName="button-wrapper">
        {training?.is_published ? (
          <>
            <Button to={generateTrainingUrl(training)} color="white">
              {translate('general.terms.cancel')}
            </Button>
            <Button onClick={handleConfirmModal}>
              {translate('general.terms.update')}
              {loading && saveAndViewLoader && (
                <FontAwesomeIcon icon={faSpinner} spin />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => save(true)}
              color="white"
              inactive={loading && (saveAndViewLoader || saveLoader)}
            >
              {translate('general.terms.save-and-view')}
              {loading && saveAndViewLoader && (
                <FontAwesomeIcon icon={faSpinner} spin />
              )}
            </Button>
            <Button
              onClick={() => save()}
              inactive={loading && (saveAndViewLoader || saveLoader)}
            >
              {translate('general.terms.save')}
              {loading && saveLoader && (
                <FontAwesomeIcon icon={faSpinner} spin />
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
